import {
  DESCRIPCION_TIPO_DE_OPERACION,
  DESCRIPCION_TIPO_DE_OPERACION_EN,
  ETIQUETA_TIPO_DE_OPERACION,
  ETIQUETA_TIPO_DE_OPERACION_EN,
  PRIMERA_HORA_DEL_PLANEADOR,
  TIPOS_DE_OPERACION_DE_BODEGA,
  ULTIMA_HORA_DEL_PLANEADOR,
  type EstadoDeOperacionDeBodega,
  type OperacionDeBodega,
  type RenglonDeSemana,
  type ResultadoDeCargaDelPlaneador,
  type SemanaDelPlaneador,
  type TipoDeOperacionDeBodega,
} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  rutaExcelDeSemana,
  useCargarPlaneador,
  useCrearOperacion,
  useEditarOperacion,
  useEliminarOperacion,
  useExportacionesPorLlenar,
  useGuardarSemana,
  useHistoricoDelPlaneador,
  useSemanaDelPlaneador,
} from '../api/planeador';
import {AYUDA_PLANEADOR as A} from '../componentes/ayuda-planeador';
import {CampoDeArchivo} from '../componentes/elegir-archivo';
import {OPCION_DE_MENU} from '../componentes/menu-de-herramientas';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {et, localeActual, tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

const campo = 'mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';
const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';
const mini = 'rounded border border-linea bg-white px-1.5 text-xs hover:bg-papel disabled:opacity-40';

const aFecha = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const sumarDias = (fecha: string, dias: number) => {
  const d = new Date(`${fecha}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return aFecha(d);
};
const lunesDe = (fecha: string) => sumarDias(fecha, -((new Date(`${fecha}T12:00:00`).getDay() + 6) % 7));
const hoy = () => aFecha(new Date());
const nombreDelDia = (fecha: string, opciones: Intl.DateTimeFormatOptions) =>
  new Date(`${fecha}T12:00:00`).toLocaleDateString(localeActual(), opciones);
const horaTexto = (h: number | null) => (h === null ? tr('Sin hora', 'No time') : `${String(h).padStart(2, '0')}:00`);
const HORAS = Array.from(
  {length: ULTIMA_HORA_DEL_PLANEADOR - PRIMERA_HORA_DEL_PLANEADOR + 1},
  (_, i) => PRIMERA_HORA_DEL_PLANEADOR + i,
);

// Planeador semanal de la bodega de Cartagena (decision 0063, Daniel
// 16/10/2026): las operaciones de cada dia (llenados con su exportacion,
// descargues CM y MA, Antonov, muestreos...) con su hora opcional; maximo 8
// por dia. Esta semana, la siguiente, cualquier otra y el historico.
export function PaginaPlaneador() {
  const permisos = usePermisos();
  const [parametros, setParametros] = useSearchParams();
  const vista = parametros.get('vista') === 'historico' ? 'historico' : 'semana';
  const semana = lunesDe(parametros.get('semana') ?? hoy());
  const estaSemana = lunesDe(hoy());
  const proxima = sumarDias(estaSemana, 7);
  const ir = (cambios: Record<string, string | null>) => {
    const n = new URLSearchParams(parametros);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) n.set(k, v);
      else n.delete(k);
    }
    setParametros(n, {replace: true});
  };
  const [cargando, setCargando] = useState(false);
  const activo = (si: boolean) =>
    si ? 'border-dorado text-verde-oscuro' : 'border-transparent text-apagado hover:text-tinta';

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Planeador semanal', 'Weekly planner')}</h1>
          <p className="text-sm text-apagado">
            {tr(
              'Las operaciones de la bodega de Cartagena por día y hora: llenados con su exportación, descargues CM (Colombian Mint y Mona Minas) y MA (Mineros), Antonov y muestreos. Caben 8 operaciones por día.',
              'Cartagena warehouse operations by day and time: fillings with their export, CM (Colombian Mint and Mona Minas) and MA (Mineros) unloadings, Antonov and sampling. Up to 8 operations fit per day.',
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {vista === 'semana' && (
            <a href={rutaExcelDeSemana(semana)} className={botonSuave} download>
              ⬇ {tr('Excel de la semana', 'Week Excel')}
            </a>
          )}
          {permisos.editar && (
            <button type="button" className={botonSuave} onClick={() => setCargando(c => !c)}>
              ⇪ {tr('Cargar planificador (Excel)', 'Upload planner (Excel)')}
            </button>
          )}
        </div>
      </div>
      {cargando && <CargaDelPlaneador onCerrar={() => setCargando(false)} />}

      <nav className="flex flex-wrap items-center gap-1 border-b border-linea" aria-label={tr('Semanas', 'Weeks')}>
        <button
          type="button"
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${activo(vista === 'semana' && semana === estaSemana)}`}
          onClick={() => ir({vista: null, semana: null})}
        >
          {tr('Esta semana', 'This week')}
        </button>
        <button
          type="button"
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${activo(vista === 'semana' && semana === proxima)}`}
          onClick={() => ir({vista: null, semana: proxima})}
        >
          {tr('Próxima semana', 'Next week')}
        </button>
        <button
          type="button"
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${activo(vista === 'historico')}`}
          onClick={() => ir({vista: 'historico'})}
        >
          {tr('Histórico', 'History')}
        </button>
        {vista === 'semana' && (
          <span className="ml-auto flex items-center gap-1 pb-1 text-sm">
            <button type="button" className={mini} onClick={() => ir({semana: sumarDias(semana, -7)})}>
              ← {tr('Anterior', 'Previous')}
            </button>
            <input
              type="date"
              value={semana}
              onChange={e => e.target.value && ir({semana: lunesDe(e.target.value)})}
              aria-label={tr('Ir a la semana del', 'Go to the week of')}
              className="rounded-md border border-linea bg-white px-2 py-0.5 text-sm"
            />
            <button type="button" className={mini} onClick={() => ir({semana: sumarDias(semana, 7)})}>
              {tr('Siguiente', 'Next')} →
            </button>
          </span>
        )}
      </nav>

      {vista === 'historico' ? (
        <Historico onAbrir={inicio => ir({vista: null, semana: inicio})} />
      ) : (
        <VistaDeSemana key={semana} fecha={semana} />
      )}
    </section>
  );
}

function VistaDeSemana({fecha}: {fecha: string}) {
  const consulta = useSemanaDelPlaneador(fecha);
  if (consulta.isError) return <p className="text-sm text-alerta">{consulta.error.message}</p>;
  if (!consulta.data) return <p className="text-sm text-apagado">{tr('Cargando la semana…', 'Loading the week…')}</p>;
  const s = consulta.data;
  return (
    <div className="space-y-4" data-prueba="semana-del-planeador">
      <p className="text-sm text-apagado">
        {tr(
          `Semana del ${nombreDelDia(s.inicio, {day: 'numeric', month: 'long'})} al ${nombreDelDia(s.fin, {day: 'numeric', month: 'long', year: 'numeric'})}`,
          `Week from ${nombreDelDia(s.inicio, {day: 'numeric', month: 'long'})} to ${nombreDelDia(s.fin, {day: 'numeric', month: 'long', year: 'numeric'})}`,
        )}
        {s.archivo && (
          <span className="ml-2 text-xs">· {tr(`cargada de ${s.archivo}`, `loaded from ${s.archivo}`)}</span>
        )}
      </p>
      <div className="grid gap-2 lg:grid-cols-7">
        {s.dias.map(d => (
          <DiaDelPlaneador key={d.fecha} semana={s} fecha={d.fecha} />
        ))}
      </div>
      <ListasDeLaSemana key={s.inicio} semana={s} />
    </div>
  );
}

// Mover una operacion a otro dia arrastrandola (Daniel, 23/10/2026): la hora
// se conserva; se guarda con su version, como "Cambiar".
const TIPO_DE_ARRASTRE = 'application/x-cnc-operacion';
type OperacionArrastrada = {id: number; version: number; fecha: string};

function DiaDelPlaneador({semana, fecha}: {semana: SemanaDelPlaneador; fecha: string}) {
  const permisos = usePermisos();
  const editar = useEditarOperacion();
  const [agregando, setAgregando] = useState(false);
  const [encima, setEncima] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dia = semana.dias.find(d => d.fecha === fecha)!;
  const operaciones = semana.operaciones.filter(o => o.fecha === fecha);
  const esHoy = fecha === semana.hoy;
  const recibe = (e: React.DragEvent) => permisos.editar && e.dataTransfer.types.includes(TIPO_DE_ARRASTRE);
  const soltar = (e: React.DragEvent) => {
    if (!recibe(e)) return;
    e.preventDefault();
    setEncima(false);
    let datos: OperacionArrastrada;
    try {
      datos = JSON.parse(e.dataTransfer.getData(TIPO_DE_ARRASTRE)) as OperacionArrastrada;
    } catch {
      return;
    }
    if (datos.fecha === fecha) return;
    setError(null);
    editar
      .mutateAsync({id: datos.id, cambios: {version: datos.version, fecha}})
      .catch(err => setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo mover.', 'Could not move it.')));
  };
  return (
    <div
      className={`flex min-h-40 flex-col rounded-lg border bg-white p-2 ${
        encima ? 'border-verde ring-2 ring-verde/40' : esHoy ? 'border-dorado' : 'border-linea'
      } ${editar.isPending ? 'opacity-70' : ''}`}
      role="region"
      aria-label={nombreDelDia(fecha, {weekday: 'long', day: 'numeric', month: 'long'})}
      data-prueba="dia-del-planeador"
      onDragOver={e => {
        if (!recibe(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setEncima(true);
      }}
      onDragLeave={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEncima(false);
      }}
      onDrop={soltar}
    >
      <div className="flex items-baseline justify-between gap-1 border-b border-linea pb-1">
        <span className="text-sm font-semibold capitalize text-verde-oscuro">
          {nombreDelDia(fecha, {weekday: 'long', day: 'numeric', month: 'short'})}
        </span>
        <span
          className={`rounded-full px-1.5 text-xs font-semibold tabular-nums ${
            dia.sobreCapacidad ? 'bg-alerta text-white' : 'bg-papel text-apagado'
          }`}
          title={tr('Operaciones del día y máximo', 'Operations of the day and maximum')}
          data-prueba="conteo-del-dia"
        >
          {tr(`${dia.operaciones} de ${semana.capacidadPorDia}`, `${dia.operaciones} of ${semana.capacidadPorDia}`)}
        </span>
      </div>
      <ul className="mt-1 flex-1 space-y-1">
        {operaciones.map(o => (
          <OperacionEnLista key={o.id} operacion={o} />
        ))}
        {!operaciones.length && <li className="text-xs text-apagado">{tr('Sin operaciones', 'No operations')}</li>}
      </ul>
      {error && <p className="text-xs text-alerta">{error}</p>}
      {permisos.editar &&
        (agregando ? (
          <FormularioDeOperacion fecha={fecha} onCerrar={() => setAgregando(false)} />
        ) : (
          <button type="button" className={`${mini} mt-1 self-start`} onClick={() => setAgregando(true)}>
            ＋ {tr('Agregar', 'Add')}
          </button>
        ))}
    </div>
  );
}

const MARCA: Record<EstadoDeOperacionDeBodega, string> = {planeada: '', hecha: '✔', no_se_hizo: '✖'};

function OperacionEnLista({operacion: o}: {operacion: OperacionDeBodega}) {
  const permisos = usePermisos();
  const editar = useEditarOperacion();
  const eliminar = useEliminarOperacion();
  const [editando, setEditando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cambiarEstado = (estado: EstadoDeOperacionDeBodega) =>
    editar
      .mutateAsync({id: o.id, cambios: {version: o.version, estado: o.estado === estado ? 'planeada' : estado}})
      .catch(e => setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.')));
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (evento: MouseEvent) => {
      if (caja.current && !caja.current.contains(evento.target as Node)) setAbierto(false);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);
  if (editando) return <FormularioDeOperacion fecha={o.fecha} operacion={o} onCerrar={() => setEditando(false)} />;
  const detalle = o.exportacion ?? o.referencia;
  // Linea minima (Daniel, 19/10/2026): hora y actividad en una sola linea; el
  // detalle y las acciones van en el menu de la flecha.
  const masDatos = [
    o.exportador,
    o.bigBags !== null ? `${numero(o.bigBags)} BB` : null,
    o.cuenta > 1 ? `×${o.cuenta}` : null,
  ].filter(Boolean);
  const accion = (hacer: () => void) => () => {
    setAbierto(false);
    hacer();
  };
  return (
    <li
      ref={caja}
      className={`relative rounded px-1 py-0.5 text-xs hover:bg-papel ${permisos.editar ? 'cursor-grab active:cursor-grabbing' : ''}`}
      title={[
        ...masDatos,
        o.notas,
        permisos.editar ? tr('Arrástrala a otro día para moverla', 'Drag it to another day to move it') : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      data-prueba="operacion-de-bodega"
      draggable={permisos.editar && !abierto}
      onDragStart={e => {
        const datos: OperacionArrastrada = {id: o.id, version: o.version, fecha: o.fecha};
        e.dataTransfer.setData(TIPO_DE_ARRASTRE, JSON.stringify(datos));
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <div className="flex items-start gap-1.5">
        <span className="w-9 shrink-0 tabular-nums text-apagado">{o.hora === null ? '—' : horaTexto(o.hora)}</span>
        <span
          className={`min-w-0 flex-1 break-words ${
            o.estado === 'hecha'
              ? 'text-verde-oscuro'
              : o.estado === 'no_se_hizo'
                ? 'text-apagado line-through decoration-alerta/60'
                : ''
          }`}
        >
          <span className="font-medium">{o.tipoEtiqueta}</span>
          {detalle && <span> {detalle}</span>}
          {MARCA[o.estado] && (
            <span className={`ml-1 ${o.estado === 'hecha' ? 'text-verde' : 'text-alerta'}`}>{MARCA[o.estado]}</span>
          )}
        </span>
        <button
          type="button"
          className="shrink-0 rounded px-1 text-sm leading-4 text-apagado hover:bg-white hover:text-tinta"
          onClick={() => setAbierto(a => !a)}
          aria-expanded={abierto}
          aria-haspopup="true"
          aria-label={tr(`Opciones: ${o.tipoEtiqueta}`, `Options: ${o.tipoEtiqueta}`)}
        >
          ▾
        </button>
      </div>
      {abierto && (
        <div
          role="group"
          aria-label={tr(`Opciones: ${o.tipoEtiqueta}`, `Options: ${o.tipoEtiqueta}`)}
          className="absolute left-0 z-20 mt-1 flex w-56 flex-col gap-0.5 rounded-lg border border-linea bg-white p-1 text-sm no-underline shadow-lg"
        >
          {(masDatos.length > 0 || o.notas || o.textoOriginal) && (
            <div className="space-y-0.5 border-b border-linea px-2 py-1 text-xs text-apagado">
              {masDatos.length > 0 && <p className="text-tinta">{masDatos.join(' · ')}</p>}
              {o.notas && <p>{o.notas}</p>}
              {o.textoOriginal && <p>{tr(`Del Excel: ${o.textoOriginal}`, `From Excel: ${o.textoOriginal}`)}</p>}
            </div>
          )}
          {permisos.editar ? (
            <>
              <button
                type="button"
                className={OPCION_DE_MENU}
                disabled={editar.isPending}
                onClick={accion(() => void cambiarEstado('hecha'))}
                aria-label={tr(`Marcar hecha: ${o.tipoEtiqueta}`, `Mark done: ${o.tipoEtiqueta}`)}
              >
                ✔ {o.estado === 'hecha' ? tr('Quitar "hecha"', 'Undo "done"') : tr('Hecha', 'Done')}
              </button>
              <button
                type="button"
                className={OPCION_DE_MENU}
                disabled={editar.isPending}
                onClick={accion(() => void cambiarEstado('no_se_hizo'))}
                aria-label={tr(`Marcar que no se hizo: ${o.tipoEtiqueta}`, `Mark not done: ${o.tipoEtiqueta}`)}
              >
                ✖{' '}
                {o.estado === 'no_se_hizo'
                  ? tr('Quitar "no se hizo"', 'Undo "not done"')
                  : tr('No se hizo', 'Not done')}
              </button>
              <button
                type="button"
                className={OPCION_DE_MENU}
                onClick={accion(() => setEditando(true))}
                aria-label={tr(`Cambiar ${o.tipoEtiqueta}`, `Change ${o.tipoEtiqueta}`)}
              >
                ✎ {tr('Cambiar', 'Change')}
              </button>
              <button
                type="button"
                className={OPCION_DE_MENU}
                disabled={eliminar.isPending}
                onClick={accion(() => {
                  if (window.confirm(tr(`¿Quitar "${o.tipoEtiqueta}"?`, `Remove "${o.tipoEtiqueta}"?`)))
                    void eliminar
                      .mutateAsync(o.id)
                      .catch(e => setError(e instanceof ErrorDeApi ? e.message : String(e)));
                })}
                aria-label={tr(`Quitar ${o.tipoEtiqueta}`, `Remove ${o.tipoEtiqueta}`)}
              >
                🗑 {tr('Quitar', 'Remove')}
              </button>
            </>
          ) : (
            !masDatos.length &&
            !o.notas &&
            !o.textoOriginal && (
              <p className="px-2 py-1 text-xs text-apagado">{tr('Sin más datos.', 'No more details.')}</p>
            )
          )}
        </div>
      )}
      {error && <p className="text-alerta">{error}</p>}
    </li>
  );
}

function FormularioDeOperacion({
  fecha,
  operacion,
  onCerrar,
}: {
  fecha: string;
  operacion?: OperacionDeBodega;
  onCerrar: () => void;
}) {
  const crear = useCrearOperacion();
  const editar = useEditarOperacion();
  const porLlenar = useExportacionesPorLlenar();
  const [tipo, setTipo] = useState<TipoDeOperacionDeBodega>(operacion?.tipo ?? 'llenado');
  const [hora, setHora] = useState(
    operacion?.hora === undefined || operacion.hora === null ? '' : String(operacion.hora),
  );
  const [exportacion, setExportacion] = useState(operacion?.exportacion ?? '');
  const [referencia, setReferencia] = useState(operacion?.exportacion ? '' : (operacion?.referencia ?? ''));
  const [bigBags, setBigBags] = useState(operacion?.bigBags === null || !operacion ? '' : String(operacion.bigBags));
  const [cuenta, setCuenta] = useState(String(operacion?.cuenta ?? 1));
  const [notas, setNotas] = useState(operacion?.notas ?? '');
  const [dia, setDia] = useState(operacion?.fecha ?? fecha);
  const [error, setError] = useState<string | null>(null);
  const etiquetas = et(ETIQUETA_TIPO_DE_OPERACION, ETIQUETA_TIPO_DE_OPERACION_EN);
  const descripciones = et(DESCRIPCION_TIPO_DE_OPERACION, DESCRIPCION_TIPO_DE_OPERACION_EN);
  const ocupado = crear.isPending || editar.isPending;

  const guardar = async () => {
    setError(null);
    const datos = {
      fecha: dia,
      hora: hora === '' ? null : Number(hora),
      tipo,
      exportacion: tipo === 'llenado' && exportacion ? exportacion : null,
      referencia: referencia.trim() || null,
      bigBags: bigBags === '' ? null : Number(bigBags),
      cuenta: Number(cuenta) || 1,
      notas: notas.trim() || null,
    };
    try {
      if (operacion) await editar.mutateAsync({id: operacion.id, cambios: {...datos, version: operacion.version}});
      else await crear.mutateAsync({...datos, estado: 'planeada'});
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  return (
    <form
      className="mt-1 space-y-1 rounded border border-verde/40 bg-white p-1.5 text-xs"
      aria-label={operacion ? tr('Cambiar operación', 'Change operation') : tr('Nueva operación', 'New operation')}
      onSubmit={e => {
        e.preventDefault();
        void guardar();
      }}
    >
      <label className="block text-apagado">
        {tr('Tipo', 'Type')}
        <NotaDeAyuda texto={A.tipo} dentroDeEtiqueta />
        <select
          value={tipo}
          onChange={e => setTipo(e.target.value as TipoDeOperacionDeBodega)}
          className={campo}
          title={descripciones[tipo]}
        >
          {TIPOS_DE_OPERACION_DE_BODEGA.map(t => (
            <option key={t} value={t}>
              {etiquetas[t]}
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-1">
        <label className="block text-apagado">
          {tr('Hora', 'Time')}
          <select value={hora} onChange={e => setHora(e.target.value)} className={campo}>
            <option value="">{tr('Sin hora', 'No time')}</option>
            {HORAS.map(h => (
              <option key={h} value={h}>
                {horaTexto(h)}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-apagado">
          {tr('Día', 'Day')}
          <input type="date" value={dia} onChange={e => setDia(e.target.value)} className={campo} required />
        </label>
      </div>
      {tipo === 'llenado' && (
        <label className="block text-apagado">
          {tr('Exportación', 'Export')}
          <NotaDeAyuda texto={A.exportacion} dentroDeEtiqueta />
          <select value={exportacion} onChange={e => setExportacion(e.target.value)} className={campo}>
            <option value="">{tr('— Sin exportación (escribir abajo) —', '— No export (write below) —')}</option>
            {exportacion && !porLlenar.data?.exportaciones.some(x => x.codigo === exportacion) && (
              <option value={exportacion}>{exportacion}</option>
            )}
            {porLlenar.data?.exportaciones.map(x => (
              <option key={x.codigo} value={x.codigo}>
                {x.codigo}
                {x.exportador ? ` · ${x.exportador}` : ''}
                {x.etd ? ` · ETD ${x.etd}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {!(tipo === 'llenado' && exportacion) && (
        <label className="block text-apagado">
          {tr('Referencia', 'Reference')}
          <NotaDeAyuda texto={A.referencia} dentroDeEtiqueta />
          <input value={referencia} onChange={e => setReferencia(e.target.value)} className={campo} maxLength={200} />
        </label>
      )}
      <div className="grid grid-cols-2 gap-1">
        <label className="block text-apagado">
          Big Bags
          <input type="number" min={0} value={bigBags} onChange={e => setBigBags(e.target.value)} className={campo} />
        </label>
        <label className="block text-apagado">
          {tr('Cuenta como', 'Counts as')}
          <NotaDeAyuda texto={A.cuenta} dentroDeEtiqueta />
          <select value={cuenta} onChange={e => setCuenta(e.target.value)} className={campo}>
            {[1, 2, 3, 4].map(n => (
              <option key={n} value={n}>
                {n === 1 ? tr('1 operación', '1 operation') : tr(`${n} operaciones`, `${n} operations`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-apagado">
        {tr('Notas', 'Notes')}
        <input value={notas} onChange={e => setNotas(e.target.value)} className={campo} maxLength={1000} />
      </label>
      {error && (
        <p role="alert" className="text-alerta">
          {error}
        </p>
      )}
      <div className="flex gap-1">
        <button type="submit" className={`${mini} border-verde text-verde-oscuro`} disabled={ocupado}>
          {tr('Guardar', 'Save')}
        </button>
        <button type="button" className={mini} onClick={onCerrar}>
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

// Objetivos, pendientes, fechas limite, notas y maximo por dia de la semana.
function ListasDeLaSemana({semana: s}: {semana: SemanaDelPlaneador}) {
  const permisos = usePermisos();
  const guardar = useGuardarSemana();
  const [objetivos, setObjetivos] = useState(s.objetivos);
  const [pendientes, setPendientes] = useState(s.pendientes);
  const [fechasLimite, setFechasLimite] = useState(s.fechasLimite);
  const [notas, setNotas] = useState(s.notas ?? '');
  const [capacidad, setCapacidad] = useState(String(s.capacidadPorDia));
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const enviar = async () => {
    setMensaje(null);
    const limpiar = (l: RenglonDeSemana[]) => l.filter(r => r.texto.trim()).map(r => ({...r, texto: r.texto.trim()}));
    try {
      await guardar.mutateAsync({
        fecha: s.inicio,
        cambios: {
          version: s.version,
          objetivos: limpiar(objetivos),
          pendientes: limpiar(pendientes),
          fechasLimite: limpiar(fechasLimite),
          notas: notas.trim() || null,
          capacidadPorDia: Number(capacidad) || 8,
        },
      });
      setMensaje({texto: tr('Semana guardada.', 'Week saved.'), error: false});
    } catch (e) {
      setMensaje({
        texto: e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'),
        error: true,
      });
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="listas-de-la-semana">
      <div className="grid gap-4 md:grid-cols-3">
        <Lista titulo={tr('Objetivos semanales', 'Weekly goals')} lista={objetivos} onCambiar={setObjetivos} />
        <Lista titulo={tr('Elementos pendientes', 'Pending items')} lista={pendientes} onCambiar={setPendientes} />
        <Lista
          titulo={tr('Fechas límite esta semana', 'Deadlines this week')}
          lista={fechasLimite}
          onCambiar={setFechasLimite}
        />
      </div>
      <div className="grid gap-3 md:grid-cols-[1fr_auto]">
        <label className="block text-xs text-apagado">
          {tr('Notas', 'Notes')}
          <textarea
            value={notas}
            onChange={e => setNotas(e.target.value)}
            rows={2}
            disabled={!permisos.editar}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Máximo por día', 'Maximum per day')}
          <NotaDeAyuda texto={A.capacidad} dentroDeEtiqueta />
          <input
            type="number"
            min={1}
            max={30}
            value={capacidad}
            onChange={e => setCapacidad(e.target.value)}
            disabled={!permisos.editar}
            className={`${campo} w-24`}
          />
        </label>
      </div>
      {mensaje && (
        <p
          role={mensaje.error ? 'alert' : 'status'}
          className={`text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}
        >
          {mensaje.texto}
        </p>
      )}
      {permisos.editar && (
        <button type="button" className={boton} disabled={guardar.isPending} onClick={() => void enviar()}>
          {tr('Guardar la semana', 'Save the week')}
        </button>
      )}
    </div>
  );
}

function Lista({
  titulo,
  lista,
  onCambiar,
}: {
  titulo: string;
  lista: RenglonDeSemana[];
  onCambiar: (l: RenglonDeSemana[]) => void;
}) {
  const permisos = usePermisos();
  const poner = (i: number, r: Partial<RenglonDeSemana>) =>
    onCambiar(lista.map((x, j) => (j === i ? {...x, ...r} : x)));
  return (
    <div>
      <h3 className="text-xs font-semibold tracking-wide text-apagado uppercase">{titulo}</h3>
      <ul className="mt-1 space-y-1">
        {lista.map((r, i) => (
          <li key={i} className="flex items-center gap-1">
            <select
              value={r.hecho === true ? 'si' : r.hecho === false ? 'no' : ''}
              onChange={e => poner(i, {hecho: e.target.value === 'si' ? true : e.target.value === 'no' ? false : null})}
              disabled={!permisos.editar}
              aria-label={tr(`Marca de ${r.texto || titulo}`, `Mark of ${r.texto || titulo}`)}
              className="rounded border border-linea bg-white px-1 text-xs"
            >
              <option value="">—</option>
              <option value="si">✔</option>
              <option value="no">✖</option>
            </select>
            <input
              value={r.texto}
              onChange={e => poner(i, {texto: e.target.value})}
              disabled={!permisos.editar}
              aria-label={titulo}
              className="min-w-0 flex-1 rounded border border-linea px-1.5 py-0.5 text-sm"
              maxLength={500}
            />
            {permisos.editar && (
              <button
                type="button"
                className={mini}
                onClick={() => onCambiar(lista.filter((_, j) => j !== i))}
                aria-label={tr('Quitar renglón', 'Remove line')}
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>
      {permisos.editar && (
        <button
          type="button"
          className={`${mini} mt-1`}
          onClick={() => onCambiar([...lista, {texto: '', hecho: null}])}
        >
          ＋ {tr('Renglón', 'Line')}
        </button>
      )}
    </div>
  );
}

const GRUPOS: Array<{nombre: () => string; tipos: TipoDeOperacionDeBodega[]}> = [
  {nombre: () => tr('Llenados', 'Fillings'), tipos: ['llenado']},
  {nombre: () => tr('Descargues CM', 'CM unloadings'), tipos: ['descargue_cm']},
  {nombre: () => tr('Descargues MA', 'MA unloadings'), tipos: ['descargue_ma']},
  {nombre: () => 'Antonov', tipos: ['antonov']},
  {nombre: () => tr('Muestreos', 'Sampling'), tipos: ['muestreo']},
  {
    nombre: () => tr('Otras', 'Others'),
    tipos: ['furgon', 'descargue_qu', 'descargue_msa', 'descargue_mns', 'descargue_ipm', 'otra'],
  },
];

function Historico({onAbrir}: {onAbrir: (inicio: string) => void}) {
  const consulta = useHistoricoDelPlaneador();
  if (consulta.isError) return <p className="text-sm text-alerta">{consulta.error.message}</p>;
  if (!consulta.data) return <p className="text-sm text-apagado">{tr('Cargando…', 'Loading…')}</p>;
  const semanas = consulta.data.semanas;
  if (!semanas.length)
    return (
      <p className="text-sm text-apagado">
        {tr(
          'Todavía no hay semanas guardadas. Arma esta semana o carga los Excel del planificador.',
          'There are no saved weeks yet. Plan this week or upload the planner Excel files.',
        )}
      </p>
    );
  return (
    <div className="overflow-auto rounded-lg border border-linea bg-white" data-prueba="historico-del-planeador">
      <table className="w-full text-sm">
        <thead className="bg-papel">
          <tr className="text-left text-xs tracking-wide text-apagado uppercase">
            <th className="px-3 py-2 font-semibold">{tr('Semana', 'Week')}</th>
            <th className="px-3 py-2 text-right font-semibold">{tr('Operaciones', 'Operations')}</th>
            {GRUPOS.map(g => (
              <th key={g.nombre()} className="px-3 py-2 text-right font-semibold">
                {g.nombre()}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-semibold">
              {tr('Días sobre el máximo', 'Days over the maximum')}
              <NotaDeAyuda texto={A.sobreMaximo} />
            </th>
            <th className="px-3 py-2 font-semibold">{tr('Origen', 'Source')}</th>
          </tr>
        </thead>
        <tbody>
          {semanas.map(s => (
            <tr key={s.inicio} className="border-t border-linea/70">
              <td className="px-3 py-1.5">
                <button type="button" className="text-verde hover:underline" onClick={() => onAbrir(s.inicio)}>
                  {nombreDelDia(s.inicio, {day: 'numeric', month: 'short', year: 'numeric'})}
                </button>
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">{numero(s.operaciones)}</td>
              {GRUPOS.map(g => (
                <td key={g.nombre()} className="px-3 py-1.5 text-right tabular-nums">
                  {numero(g.tipos.reduce((a, t) => a + (s.porTipo[t] ?? 0), 0))}
                </td>
              ))}
              <td
                className={`px-3 py-1.5 text-right tabular-nums ${s.diasSobreCapacidad ? 'font-semibold text-alerta' : ''}`}
              >
                {numero(s.diasSobreCapacidad)}
              </td>
              <td className="px-3 py-1.5 text-xs text-apagado">
                {s.origen === 'planificador_excel' ? (s.archivo ?? 'Excel') : tr('App', 'App')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACCION: Record<ResultadoDeCargaDelPlaneador['semanas'][number]['accion'], () => string> = {
  nueva: () => tr('Nueva', 'New'),
  reemplaza: () => tr('Reemplaza la cargada antes', 'Replaces the one loaded before'),
  igual: () => tr('Se vuelve a cargar', 'Loaded again'),
  protegida: () => tr('No se toca: se cambió en la app', 'Not touched: changed in the app'),
  repetida: () => tr('Repetida: manda otro archivo más reciente', 'Repeated: a newer file wins'),
};

// Carga de los Excel del Planificador Semanal Bulkmatic (varios a la vez).
export function CargaDelPlaneador({onCerrar}: {onCerrar?: () => void}) {
  const cargar = useCargarPlaneador();
  const [archivos, setArchivos] = useState<File[]>([]);
  const [resultado, setResultado] = useState<ResultadoDeCargaDelPlaneador | null>(null);
  const [error, setError] = useState<string | null>(null);
  const enviar = async (modo: 'revisar' | 'aplicar') => {
    setError(null);
    try {
      setResultado(await cargar.mutateAsync({archivos, modo}));
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo leer.', 'Could not read.'));
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-verde/40 bg-white p-4" data-prueba="carga-del-planeador">
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-medium">
          {tr('Cargar el Planificador Semanal (Excel)', 'Upload the Weekly Planner (Excel)')}
        </h2>
        {onCerrar && (
          <button type="button" onClick={onCerrar} aria-label={tr('Cerrar', 'Close')} className="text-apagado">
            ×
          </button>
        )}
      </div>
      <p className="text-xs text-apagado">
        {tr(
          'Puedes elegir todos los archivos de la carpeta a la vez. Si dos traen la misma semana, manda el más reciente. Las semanas que ya se cambiaron en la app no se tocan. Primero revisa; nada se guarda hasta que oprimas "Guardar".',
          'You can choose all the files of the folder at once. If two bring the same week, the newest wins. Weeks already changed in the app are not touched. Review first; nothing is saved until you press "Save".',
        )}
      </p>
      <CampoDeArchivo
        etiqueta={tr('Archivos del planificador', 'Planner files')}
        accept=".xlsx"
        multiple
        archivos={archivos}
        onElegir={a => {
          setArchivos(a);
          setResultado(null);
        }}
      />
      <div className="flex gap-2">
        <button
          type="button"
          className={botonSuave}
          disabled={!archivos.length || cargar.isPending}
          onClick={() => void enviar('revisar')}
        >
          {tr('Revisar', 'Review')}
        </button>
        {resultado && !resultado.aplicado && (
          <button type="button" className={boton} disabled={cargar.isPending} onClick={() => void enviar('aplicar')}>
            {tr('Guardar', 'Save')}
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-sm text-alerta">
          {error}
        </p>
      )}
      {resultado && (
        <div className="space-y-1">
          {resultado.aplicado && (
            <p role="status" className="text-sm text-verde-oscuro">
              {tr('Listo: el planificador quedó cargado.', 'Done: the planner was loaded.')}
            </p>
          )}
          {resultado.avisos.map(a => (
            <p key={a} className="text-xs text-apagado">
              ⚠ {a}
            </p>
          ))}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-wide text-apagado uppercase">
                <th className="py-1 font-semibold">{tr('Semana', 'Week')}</th>
                <th className="py-1 font-semibold">{tr('Archivo', 'File')}</th>
                <th className="py-1 text-right font-semibold">{tr('Operaciones', 'Operations')}</th>
                <th className="py-1 text-right font-semibold">
                  {tr('Exportaciones reconocidas', 'Exports recognized')}
                </th>
                <th className="py-1 pl-3 font-semibold">{tr('Qué pasa', 'What happens')}</th>
              </tr>
            </thead>
            <tbody>
              {resultado.semanas.map(s => (
                <tr key={`${s.inicio}-${s.archivo}`} className="border-t border-linea/70">
                  <td className="py-1">{s.inicio}</td>
                  <td className="py-1 text-xs">{s.archivo}</td>
                  <td className="py-1 text-right tabular-nums">{numero(s.operaciones)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(s.exportacionesReconocidas)}</td>
                  <td className="py-1 pl-3 text-xs">{ACCION[s.accion]()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
